import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import {
  SimulationDatasetApiProvider,
  useSimulationDatasetApi,
} from '../Services/SimulationDatasetApiProvider';
import { EvalDataset } from '../Services/SimulationDatasetApiProviderTypes';
import { formatDateTimeLocal } from '../utils/formatDate';
import useTablePagination from '../Hooks/useTablePagination';

function SimulationDatasetHub() {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const { getDatasets, deleteDataset } = useSimulationDatasetApi();

  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [alertShown, setAlertShown] = useState<boolean>(false);
  const [datasets, setDatasets] = useState<EvalDataset[]>([]);
  const [totalDatasets, setTotalDatasets] = useState<number>(0);

  const tableTitle = 'Simulation Dataset Hub';
  const { initialPageSize, storedSkip, storedLimit } = useTablePagination(tableTitle);

  useEffect(() => {
    if (location.state?.successMessage && !alertShown) {
      showAlert('success', location.state.successMessage);
      setAlertShown(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.successMessage, alertShown, navigate, location.pathname, showAlert]);

  const columns = [
    { name: 'Name', selector: (row: EvalDataset) => row.name, sortable: false },
    {
      name: 'Description',
      selector: (row: EvalDataset) => row.description ?? '—',
      sortable: false,
    },
    {
      name: 'Status',
      selector: (row: EvalDataset) => (row.is_active === true ? 'Active' : 'InActive'),
      sortable: false,
    },
    {
      name: 'Created',
      selector: (row: EvalDataset) => formatDateTimeLocal(row.created_at),
      sortable: false,
    },
    {
      name: 'Updated',
      selector: (row: EvalDataset) => formatDateTimeLocal(row.updated_at),
      sortable: false,
    },
  ];

  const handleRowClicked = (row: EvalDataset): void => {
    navigate(`/organizations/${organizationId}/simulation-datasets/${row.key}`, {
      state: { organizationId },
    });
  };

  const handleDelete = async (selectedIds: string[]): Promise<void> => {
    if (!selectedIds[0]) return;

    showConfirmation(`Are you sure you want to delete the selected item?`, async () => {
      try {
        setLoading(true);
        await deleteDataset(selectedIds[0]);
        await fetchDatasets(storedSkip(), storedLimit());
        showAlert('success', 'Item was successfully deleted!');
      } catch (e) {
        showAlert('fail', 'Failed to delete the item!');
      } finally {
        setLoading(false);
      }
    });
  };

  const fetchDatasets = async (skip: number, limit: number): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const response = await getDatasets(skip, limit);
      setDatasets(response.items);
      setTotalDatasets(response.totalCount);
    } catch (e) {
      setError((e as Error).message);
      showAlert('fail', 'Failed to fetch simulation datasets!');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDatasets(storedSkip(), storedLimit());
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title={tableTitle}
                columns={columns}
                data={datasets}
                selectableRows
                selectableRowsHighlight
                paginationPerPage={initialPageSize}
                progressPending={loading}
                paginationTotalRows={totalDatasets}
                onRowClicked={handleRowClicked}
                onDelete={handleDelete}
                onChangePage={fetchDatasets}
                getRowUrl={(row: EvalDataset) =>
                  `/organizations/${organizationId}/simulation-datasets/${row.key}`
                }
              />

              <Button
                background="color2"
                size="large"
                onClick={() =>
                  navigate(`/organizations/${organizationId}/simulation-datasets/new-dataset`)
                }
              >
                Create New Dataset
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function SimulationDatasetHubWrapper() {
  return (
    <ConfirmationProvider>
      <SimulationDatasetApiProvider>
        <SimulationDatasetHub />
      </SimulationDatasetApiProvider>
    </ConfirmationProvider>
  );
}

export default SimulationDatasetHubWrapper;
